import {Alert, Box, Button, createStyles, Group, Modal, Stack, Text} from '@mantine/core';
import React, {useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {debugAssert} from 'Asserts';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {TableValidationCellError} from 'notebook/structured-tables/StructuredTableApiConstants';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {buildComputedTable} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {RunModalProps} from 'ui-library/runModal';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {commitSave, getSaveBlock, LiveTable, LiveTableRegistry, SaveBlock} from './reconcile/LiveTable';
import {
  getResolution,
  IsCellReadOnly,
  keepDecisionsForDraft,
  TableConflict,
  TableConflictDecisions,
  TableConflictResolution,
} from './reconcile/mergeDraftIntoLive';
import TableConflictPanel, {TableConflictLabels} from './reconcile/TableConflictPanel';
import useHoldDocumentLock from './reconcile/useHoldDocumentLock';
import TransposedStructuredTableEditable from './TransposedStructuredTableEditable';
import {TransposePivotSpec} from './TransposePivot';

export interface PivotModalProps extends RunModalProps<void> {
  /** The table as it was when the modal opened. Edits apply to a local copy until Save. */
  structuredTable: UnknownStructuredTable;
  pivotSpec: TransposePivotSpec;
  /** Server validation errors for the table, from the note's validation snapshot. */
  validationErrors: Array<TableValidationCellError>;
  /** The entry or template the table is in. The modal holds its editor lock while open. */
  documentId: string | null;
  /** Where Save reads the notebook's current table and writes to it. */
  liveRegistry: LiveTableRegistry;
}

const SAVE_BLOCK_MESSAGES: Record<SaveBlock, string> = {
  locked: "This entry is locked, so your changes can't be saved.",
  // Nothing registers the table: it was deleted, or its toolbar unmounted.
  'lost-context': "This table is no longer available to edit, so your changes can't be saved.",
};

/** The draft and its undo/redo stacks. `present` is the draft the grid shows. */
interface DraftHistory {
  past: Array<UnknownStructuredTable>;
  present: UnknownStructuredTable;
  future: Array<UnknownStructuredTable>;
}

/**
 * Whether a cell edit is in progress, so Escape should end it rather than close the modal. Reads the
 * table engine's editing state, not the focused element: the grid keeps focus on a `contentEditable`
 * element even when no cell is being edited.
 */
function isEditingCell(target: EventTarget | null): boolean {
  return (
    spreadsheetStore.editingTable != null ||
    (target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'))
  );
}

/**
 * Full-screen editor for a table's pivot grid. Edits go to a local draft; Save writes the draft to the
 * notebook in one update and Cancel throws it away.
 */
export default function PivotModal(props: PivotModalProps): JSX.Element {
  const {structuredTable: base, pivotSpec, validationErrors, documentId, liveRegistry, close} = props;
  const {classes} = useStyles();
  const [history, setHistory] = useState<DraftHistory>({past: [], present: base, future: []});
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  // The conflicts from the last Save, and the choices made for them so far.
  const [conflicts, setConflicts] = useState<Array<TableConflict>>([]);
  const [decisions, setDecisions] = useState<TableConflictDecisions>(new Map());
  const hasSavedRef = useRef(false);

  useHoldDocumentLock(documentId);
  const getLive = (): LiveTable | null => liveRegistry.get(documentId, base.provenanceUuid);
  const live = useSyncExternalStore(liveRegistry.subscribe, getLive);
  const saveBlock = getSaveBlock(live);
  const unresolvedConflicts = conflicts.filter((conflict) => getResolution(decisions, conflict) == null);
  // Whether the Escape keydown started in a cell editor, recorded before the grid handles it.
  const escapeStartedInEditorRef = useRef(false);

  const draft = history.present;
  // Undo returns the exact `base` object, so undoing every edit makes the draft clean again.
  const isDirty = draft !== base;

  debugAssert(
    () => draft.rows.every((row) => row.cells.every((cell) => !TableUtil.isCellFormula(cell))),
    'The pivot draft must not contain formula cells'
  );

  // `buildComputedTable` reads entity resolutions from the store when it runs, so evaluate again when
  // they arrive; otherwise a newly entered entity's lookups stay blank.
  const resolutionResultsByResolutionQuery = useSelector(
    (state: BenchlingState) => state.models.eln.resolutionResultsByResolutionQuery
  );
  const computedTableRef = useRef<EvaluatedTable | null>(null);
  const computedTable = useMemo(
    () => buildComputedTable(draft, computedTableRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-evaluates when resolutions arrive
    [draft, resolutionResultsByResolutionQuery]
  );
  computedTableRef.current = computedTable;

  const classifyBaseRow = useMemo(
    () => pivotSpec.createRowClassifier(getStructuredTableAdapters()[base.kind].toTable(base)),
    [base, pivotSpec]
  );
  // Matches the cells the notebook locks, so an edit conflicts only where the notebook wouldn't take it.
  const isCellReadOnly: IsCellReadOnly = (table, rowIndex, columnIndex) =>
    getStructuredTableAdapters()[table.kind].isPositionReadOnly(columnIndex, table.rows[rowIndex], table);
  // Conflicts are labeled by the base row, which a removed row still has.
  const labelConflict = (conflict: TableConflict): TableConflictLabels =>
    classifyBaseRow(conflict.baseRowIndex);

  // The conflicts describe the draft at the last Save, so a new edit sets them aside until the next.
  // Decisions about cells the edit didn't touch still hold, so a rejected edit stays rejected.
  const setDraftConflicts = (nextDraft: UnknownStructuredTable): void => {
    setConflicts([]);
    setDecisions((current) => keepDecisionsForDraft(current, nextDraft));
  };

  const updateDraft = (next: UnknownStructuredTable): void => {
    setHistory((current) => ({past: [...current.past, current.present], present: next, future: []}));
    setDraftConflicts(next);
  };

  const restoreState = (e: ElementKeyboardEvent): boolean => {
    if (keyboard.isRedo(e)) {
      const [next, ...future] = history.future;
      if (next != null) {
        setHistory({past: [...history.past, history.present], present: next, future});
        setDraftConflicts(next);
      }
      return true;
    }
    const previous = history.past.at(-1);
    if (previous != null) {
      setHistory({
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future],
      });
      setDraftConflicts(previous);
    }
    return true;
  };

  const save = (nextDecisions: TableConflictDecisions): void => {
    // A second click can land before the modal closes; only the first one writes.
    if (hasSavedRef.current || !isDirty) {
      return;
    }
    const result = commitSave(base, draft, nextDecisions, getLive, isCellReadOnly);
    if (result.status === 'conflicts') {
      setConflicts(result.conflicts);
      setDecisions(nextDecisions);
    } else if (result.status === 'saved') {
      hasSavedRef.current = true;
      close();
    }
  };

  // Saves as soon as the last conflict is resolved.
  const resolveConflicts = (updates: Array<[TableConflict, TableConflictResolution]>): void => {
    const nextDecisions = new Map(decisions);
    for (const [conflict, resolution] of updates) {
      nextDecisions.set(conflict.key, {resolution, conflict});
    }
    if (conflicts.every((conflict) => getResolution(nextDecisions, conflict) != null)) {
      save(nextDecisions);
    } else {
      setDecisions(nextDecisions);
    }
  };

  const requestCancel = (): void => {
    if (isDirty) {
      setIsConfirmingDiscard(true);
    } else {
      close();
    }
  };

  return (
    <Box
      className={classes.root}
      onKeyDownCapture={(e) => {
        if (keyboard.isEscapeKey(e)) {
          escapeStartedInEditorRef.current = isEditingCell(e.target);
        }
      }}
      onKeyDown={(e) => {
        // Escape while editing a cell only ends the edit.
        if (keyboard.isEscapeKey(e) && !escapeStartedInEditorRef.current && !isConfirmingDiscard) {
          e.preventDefault();
          requestCancel();
        }
      }}
      {...getTestProps({component: 'PivotModal', element: 'root'})}
    >
      <Group className={classes.header} position="apart" noWrap={true}>
        {/* `Modal.Title` names the dialog. */}
        <Modal.Title>{base.name}</Modal.Title>
        <Group spacing="sm" noWrap={true}>
          <Button
            variant="secondary"
            onClick={requestCancel}
            {...getTestProps({component: 'PivotModal', element: 'cancel'})}
          >
            Cancel
          </Button>
          <Button
            disabled={!isDirty || saveBlock != null || unresolvedConflicts.length > 0}
            onClick={() => {
              save(decisions);
            }}
            {...getTestProps({component: 'PivotModal', element: 'save'})}
          >
            Save
          </Button>
        </Group>
      </Group>
      {saveBlock != null && (
        <Alert color="red" mb="sm" {...getTestProps({component: 'PivotModal', element: 'save-block'})}>
          {SAVE_BLOCK_MESSAGES[saveBlock]}
        </Alert>
      )}
      {saveBlock == null && unresolvedConflicts.length > 0 && (
        <Box mb="sm">
          <TableConflictPanel
            conflicts={unresolvedConflicts}
            labelConflict={labelConflict}
            rowHeader={pivotSpec.rowAxisLabel ?? 'Row'}
            columnHeader={pivotSpec.columnAxisLabel ?? 'Column'}
            onResolve={resolveConflicts}
          />
        </Box>
      )}
      <Box className={classes.body}>
        <TransposedStructuredTableEditable
          structuredTable={draft}
          pivotSpec={pivotSpec}
          // Read-only while Save can't write, so no edit is made that can't be kept.
          locked={saveBlock != null}
          computedTable={computedTable}
          validationErrors={validationErrors}
          onUpdateStructuredTable={updateDraft}
          // Each edit the grid commits is one undo step, recorded by `updateDraft`.
          storeState={() => {}}
          restoreState={restoreState}
        />
      </Box>
      <Modal
        opened={isConfirmingDiscard}
        onClose={() => {
          setIsConfirmingDiscard(false);
        }}
        title="Discard changes?"
        centered={true}
        // Above the pivot modal, which opens at `ZINDEX_MODAL`.
        zIndex={ZINDEX_MODAL + 1}
      >
        <Stack>
          <Text>Your changes to this table haven't been saved.</Text>
          <Group position="right" spacing="sm">
            <Button
              variant="secondary"
              onClick={() => {
                setIsConfirmingDiscard(false);
              }}
              {...getTestProps({component: 'PivotModal', element: 'keep-editing'})}
            >
              Keep editing
            </Button>
            <Button
              color="red"
              onClick={() => close()}
              {...getTestProps({component: 'PivotModal', element: 'discard'})}
            >
              Discard
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  header: {
    paddingBottom: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  body: {
    flex: 1,
    minHeight: 0,
  },
}));
